import React from 'react'
import { DeleteOutlined, MenuOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Input, notification, Typography } from 'antd'
import arrayMove from 'array-move'
import PropTypes from 'prop-types'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'

import { MAX_LABEL_LENGTH, MAX_PILLS } from '~validators'

const { Text } = Typography

const DragHandle = SortableHandle(() => (
  <MenuOutlined style={{ color: '#999', cursor: 'grab', marginRight: 12 }} />
))

const SortablePill = SortableElement(({ pill, pillIndex, onLabelChange, onUrlChange, onDelete }) => (
  <div
    style={{
      alignItems: 'center',
      background: '#fff',
      border: '1px solid #f0f0f0',
      borderRadius: 4,
      display: 'flex',
      gap: 12,
      marginBottom: 8,
      padding: '12px 16px',
      zIndex: 1100
    }}
  >
    <DragHandle />
    <div style={{ flex: '0 0 280px' }}>
      <Input
        maxLength={MAX_LABEL_LENGTH}
        onChange={event => onLabelChange(pillIndex, event.target.value)}
        placeholder="Label (e.g. Seating)"
        suffix={<Text type="secondary">{`${(pill.label || '').length}/${MAX_LABEL_LENGTH}`}</Text>}
        value={pill.label}
      />
    </div>
    <Input
      onChange={event => onUrlChange(pillIndex, event.target.value)}
      placeholder="Destination URL (e.g. /seating)"
      style={{ flex: 1 }}
      value={pill.url}
    />
    <Button
      danger
      icon={<DeleteOutlined />}
      onClick={() => onDelete(pillIndex)}
      type="text"
    />
  </div>
))

const SortablePillList = SortableContainer(({ pills, onLabelChange, onUrlChange, onDelete }) => (
  <div>
    {pills.map((pill, index) => (
      <SortablePill
        key={`popular-search-${index}`}
        index={index}
        onDelete={onDelete}
        onLabelChange={onLabelChange}
        onUrlChange={onUrlChange}
        pill={pill}
        pillIndex={index}
      />
    ))}
  </div>
))

const PopularSearchesSection = ({ pills, onChange, venueSlug }) => {
  const list = Array.isArray(pills) ? pills : []
  const exampleSlug = venueSlug ? `${venueSlug}/faqs` : 'beacon-theatre/faqs'

  const handleLabelChange = (index, value) => {
    onChange(list.map((pill, i) => (i === index ? { ...pill, label: value } : pill)))
  }

  const handleUrlChange = (index, value) => {
    onChange(list.map((pill, i) => (i === index ? { ...pill, url: value } : pill)))
  }

  const handleDelete = index => {
    onChange(list.filter((_, i) => i !== index))
  }

  const handleAdd = () => {
    if (list.length >= MAX_PILLS) {
      notification.warning({ message: `You can add up to ${MAX_PILLS} popular searches.` })
      return
    }
    onChange([...list, { label: '', url: '' }])
  }

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    onChange(arrayMove(list, oldIndex, newIndex))
  }

  return (
    <div style={{ padding: '8px 0 16px' }}>
      <h3 style={{ marginBottom: 4, fontWeight: 400, fontSize: 20 }}>Popular Searches</h3>
      <div style={{ alignItems: 'center', display: 'flex', marginBottom: 12, maxWidth: 720 }}>
        <Text type="secondary">
          {`Configure 0–${MAX_PILLS} pills. Drag to reorder. 0 hides the section on the front end. Be sure to add the venue slug where applicable eg '${exampleSlug}'`}
        </Text>
      </div>
      <SortablePillList
        onDelete={handleDelete}
        onLabelChange={handleLabelChange}
        onSortEnd={handleSortEnd}
        onUrlChange={handleUrlChange}
        pills={list}
        useDragHandle
      />
      <Button
        disabled={list.length >= MAX_PILLS}
        icon={<PlusOutlined />}
        onClick={handleAdd}
        style={{ marginTop: 8 }}
      >
        Add Popular Search
      </Button>
    </div>
  )
}

PopularSearchesSection.propTypes = {
  onChange: PropTypes.func.isRequired,
  pills: PropTypes.array,
  venueSlug: PropTypes.string
}

PopularSearchesSection.defaultProps = {
  pills: [],
  venueSlug: ''
}

export default PopularSearchesSection
